// src/pages/Dashboard.js
import { useState, useMemo, useEffect } from "react";
import { ActivityItem, HealthBadge } from "../components/UI";
import KpiCard from "../components/charts/KpiCard";
import AreaChart from "../components/charts/AreaChart";
import PeriodToggle, { PERIOD_LABELS } from "../components/charts/PeriodToggle";
import { DollarSign, Users, Target, CheckSquare } from "lucide-react";
import { LIFECYCLE_STAGES } from "../lib/constants";
import {
  watchContacts,
  watchTasks,
  watchPaymentsCollection,
  resolveContactByUserRef,
} from "../lib/db";
import { watchRecentProductActivity } from "../lib/platformActivity";
import {
  monthlyRevenueSeries,
  countPaidCustomers,
  getPaymentTimestampMs,
  paymentAmountUsd,
} from "../lib/metrics";
import { buildRevenueChartSeries } from "../lib/revenueChartSeries";
import { loadDataQuality } from "../lib/dataQualityFilters";
import { filterPayments } from "../lib/dataQualityFilters";
import { monthToDateEquivalentRanges, compareEquivalentPeriods } from "../lib/periodCompare";

function toMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

export default function Dashboard({ onOpenContact, onOpenTeamActivity }) {
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState("monthly");
  const [dataQuality] = useState(() => loadDataQuality());

  useEffect(() => {
    const u1 = watchContacts(
      (data) => {
        setLoadError(null);
        setContacts(data);
      },
      () => setLoadError("Could not load dashboard data (check Firestore rules).")
    );
    const uPay = watchPaymentsCollection(
      (rows) => setPayments(rows),
      () => {}
    );
    const u2 = watchTasks((data) => setTasks(data), () => {});
    const u3 = watchRecentProductActivity(
      (data) => setActivity(data),
      () => {},
      {
        mergeCap: 52,
        perSourceLimit: 32,
        includeActivityFeed: process.env.REACT_APP_SKIP_ACTIVITY_FEED !== "1",
      }
    );
    return () => {
      u1();
      uPay();
      u2();
      u3();
    };
  }, []);

  const paidCount = useMemo(() => countPaidCustomers(contacts, payments), [contacts, payments]);

  const revenueMtdEq = useMemo(() => {
    const { included } = filterPayments(payments, contacts, dataQuality);
    const { current, previous, labelCurrent, labelPrevious } = monthToDateEquivalentRanges();
    let cur = 0;
    let prev = 0;
    for (const p of included) {
      const t = getPaymentTimestampMs(p);
      const amt = paymentAmountUsd(p);
      if (t >= current.start && t <= current.end) cur += amt;
      if (t >= previous.start && t <= previous.end) prev += amt;
    }
    return compareEquivalentPeriods(cur, prev, { current: labelCurrent, previous: labelPrevious });
  }, [payments, contacts, dataQuality]);

  const { totals: monthTotals } = useMemo(
    () => monthlyRevenueSeries(payments, contacts, 12),
    [payments, contacts]
  );

  const revenueChart = useMemo(
    () => buildRevenueChartSeries(payments, contacts, revenuePeriod, dataQuality),
    [payments, contacts, revenuePeriod, dataQuality]
  );

  const nonInternalContacts = useMemo(() => contacts.filter((c) => !c.isInternal), [contacts]);

  const pipelineCount = useMemo(
    () => contacts.filter((c) => !c.isInternal && ["demo_scheduled", "free_scan_delivered"].includes(c.stage)).length,
    [contacts]
  );

  const atRisk = useMemo(
    () => contacts.filter((c) => !c.isInternal && c.healthScore < 40),
    [contacts]
  );
  const openTasks = useMemo(() => tasks.filter((t) => t.status === "open").length, [tasks]);
  const urgentOpen = useMemo(() => tasks.filter((t) => t.status === "open" && t.priority === "urgent").length, [tasks]);

  const stageCounts = useMemo(
    () =>
      LIFECYCLE_STAGES.map((s) => ({
        ...s,
        count: contacts.filter((c) => !c.isInternal && c.stage === s.id).length,
      })),
    [contacts]
  );

  const activityRows = useMemo(() => {
    return activity.map((ev) => {
      const fromDoc = ev.metadata?.actorName;
      const linked = ev.contactId ? resolveContactByUserRef(contacts, ev.contactId) : null;
      const fromContact = linked?.name || null;
      const fallbackUid =
        ev.contactId && !fromDoc && !fromContact
          ? `User ${String(ev.contactId).slice(0, 10)}…`
          : null;
      return {
        ev,
        subtitle: fromDoc || fromContact || fallbackUid || null,
      };
    });
  }, [activity, contacts]);

  return (
    <div>
      {loadError && (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 9, background: "var(--red-dim)", color: "var(--red)", fontSize: 13 }}>
          {loadError}
        </div>
      )}
      {/* Stats */}
      <div className="stats-grid">
        <KpiCard
          label="Invoice revenue (this month)"
          icon={DollarSign}
          accent="success"
          value={`$${Math.round(revenueMtdEq.current).toLocaleString()}`}
          trend={revenueMtdEq.growthPct}
          comparison={revenueMtdEq.comparisonText}
          sparkline={monthTotals}
        />
        <KpiCard
          label="Paid customers"
          icon={Users}
          value={String(paidCount)}
          comparison="Paid stage or ≥1 successful invoice"
        />
        <KpiCard
          label="Pipeline (demos + scans)"
          icon={Target}
          value={String(pipelineCount)}
          comparison="Active opportunities"
        />
        <KpiCard
          label="Open tasks"
          icon={CheckSquare}
          value={String(openTasks)}
          comparison={`${urgentOpen} urgent`}
        />
      </div>

      <div className="two-col" style={{ marginBottom: 20 }}>
        {/* Revenue Chart */}
        <div className="card">
          <div className="chart-card-header">
            <div>
              <div className="section-title">Revenue trend</div>
              <div className="section-sub">
                {PERIOD_LABELS[revenuePeriod]} · invoice total (excl. internal)
              </div>
            </div>
            <PeriodToggle value={revenuePeriod} onChange={setRevenuePeriod} />
          </div>
          {revenueChart.values.every((v) => v === 0) ? (
            <div className="text-caption" style={{ padding: "24px 0", textAlign: "center" }}>
              No payment rows in <code style={{ fontSize: 12 }}>payments</code> yet, or amounts don’t match filters. Set{" "}
              <code style={{ fontSize: 12 }}>REACT_APP_CRM_PAYMENT_AMOUNTS_ARE_CENTS=1</code> if Stripe stores cents.
            </div>
          ) : (
            <AreaChart values={revenueChart.values} labels={revenueChart.labels} height={320} />
          )}
        </div>

        {/* Sales funnel */}
        <div className="card">
          <div className="section-header">
            <div>
              <div className="section-title">Sales funnel</div>
              <div className="section-sub">Contacts by stage (excl. internal)</div>
            </div>
          </div>
          <div className="funnel-stage-list">
            {stageCounts.map((s) => (
              <div key={s.id}>
                <div className="funnel-stage-row-labels">
                  <span className="funnel-stage-name" style={{ color: s.color }}>{s.label}</span>
                  <span className="funnel-stage-count">
                    {s.count} contact{s.count !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="funnel-stage-track">
                  <div
                    className="funnel-stage-fill"
                    style={{
                      width: `${nonInternalContacts.length ? Math.round((s.count / nonInternalContacts.length) * 100) : 0}%`,
                      background: s.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="two-col">
        {/* At-risk */}
        <div className="card">
          <div className="section-header">
            <div>
              <div className="section-title">At-risk contacts</div>
              <div className="section-sub">Health score below 40</div>
            </div>
            <span className="badge badge-red">{atRisk.length} flagged</span>
          </div>
          {atRisk.length === 0 ? (
            <div className="text-caption" style={{ textAlign: "center", padding: "20px 0" }}>All contacts healthy</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Health</th>
                    <th>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {atRisk.map((c) => {
                    const lastMs = toMs(c.lastActiveAt);
                    const stale = lastMs != null && Date.now() - lastMs > 14 * 86400000;
                    return (
                      <tr key={c.id} onClick={() => onOpenContact(c)}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.companyName}</div>
                        </td>
                        <td>
                          <HealthBadge score={c.healthScore} />
                        </td>
                        <td style={{ fontSize: 13, color: "var(--text-2)" }}>
                          {c.paymentFailCount > 0
                            ? `Payment failed ×${c.paymentFailCount}`
                            : stale
                              ? "No login 14d+"
                              : "Low usage"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="card">
          <div className="section-header" style={{ alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="section-title">Recent activity</div>
              <div className="section-sub">All users · same feed as the Activity page (smaller sample)</div>
            </div>
            {onOpenTeamActivity && (
              <button type="button" className="btn btn-sm" onClick={onOpenTeamActivity}>
                Full team timeline
              </button>
            )}
          </div>
          {activity.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-3)", fontSize: 13 }}>
              No rows yet. Open <strong>Console</strong> for <code>permission-denied</code> or <code>[recent]</code>. Deploy{" "}
              <code>firestore.rules</code>, then <code>npm run deploy:rules</code>. If <code>activity_feed</code> is the only failure,
              set <code>REACT_APP_SKIP_ACTIVITY_FEED=1</code> and rebuild. Open the <strong>Activity</strong> page for the full
              merged timeline.
            </div>
          ) : (
            activityRows.map(({ ev, subtitle }) => (
              <ActivityItem
                key={ev.id}
                event={ev}
                subtitle={subtitle || undefined}
                onActivate={
                  ev.contactId
                    ? () => {
                        const c = resolveContactByUserRef(contacts, ev.contactId);
                        if (c) onOpenContact(c);
                      }
                    : undefined
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
